// Drobne klocki stron /konto i /admin — ten sam język co panele administracji
// motoryki (motoryka/admin/*): karty z cieniem, pola z etykietą nad inputem.

import { useState, type ReactNode } from 'react'
import { ADMIN_ROLE, LEVEL_LABEL, PASSWORD_MIN_LEN, type Level, type Role, type RoleDef } from '../../lib/auth'

export const INPUT =
  'rounded-lg border border-ink-200 bg-white px-2 py-1.5 text-sm text-ink-900 focus:border-forest-500 focus:outline-none disabled:bg-ink-50 disabled:text-ink-400'
export const BTN_PRIMARY =
  'rounded-md bg-forest-800 px-3 py-1.5 text-xs font-semibold text-white hover:bg-forest-900 disabled:opacity-40'
export const BTN_SECONDARY =
  'rounded-md border border-ink-200 bg-white px-3 py-1.5 text-xs font-medium text-ink-600 hover:bg-ink-50 disabled:opacity-40'
export const BTN_DANGER =
  'rounded-md bg-bad-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-bad-700 disabled:opacity-40'

export function Card({ title, subtitle, actions, children }: {
  title: string
  subtitle?: ReactNode
  actions?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="rounded-xl border border-ink-200 bg-white p-4 shadow-card">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-ink-900">{title}</h2>
          {subtitle && <p className="mt-0.5 text-xs text-ink-500">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  )
}

export function Field({ label, hint, children, w = 'w-full' }: {
  label: string
  hint?: string
  children: ReactNode
  w?: string
}) {
  return (
    <label className={`flex flex-col gap-1 ${w}`}>
      <span className="text-[10px] uppercase tracking-wide text-ink-400">{label}</span>
      {children}
      {hint && <span className="text-[11px] text-ink-400">{hint}</span>}
    </label>
  )
}

const NOTICE_TONE = {
  error: 'border-bad-200 bg-bad-50 text-bad-700',
  ok: 'border-good-200 bg-good-50 text-good-700',
  warn: 'border-amber-300 bg-amber-50 text-amber-900',
} as const

export function Notice({ tone, children, onClose }: {
  tone: keyof typeof NOTICE_TONE
  children: ReactNode
  onClose?: () => void
}) {
  return (
    <div className={`flex items-start justify-between gap-3 rounded-lg border px-3 py-2 text-sm ${NOTICE_TONE[tone]}`}>
      <div className="min-w-0">{children}</div>
      {onClose && (
        <button type="button" onClick={onClose} className="font-semibold" aria-label="Zamknij">✕</button>
      )}
    </div>
  )
}

/** Nowe hasło + powtórzenie z podpowiedziami (zmiana hasła na /konto, link z maila).
 *  Gotowość do wysłania: `passwordReady` z lib/auth. */
export function NewPasswordFields({ next, repeat, onNext, onRepeat, autoFocus }: {
  next: string
  repeat: string
  onNext: (v: string) => void
  onRepeat: (v: string) => void
  autoFocus?: boolean
}) {
  const tooShort = next.length > 0 && next.length < PASSWORD_MIN_LEN
  const mismatch = repeat.length > 0 && next !== repeat
  return (
    <>
      <Field
        label="Nowe hasło"
        hint={tooShort
          ? `Jeszcze ${PASSWORD_MIN_LEN - next.length} zn. — minimum ${PASSWORD_MIN_LEN}.`
          : `Co najmniej ${PASSWORD_MIN_LEN} znaków; najlepiej kilka słów.`}
      >
        <input
          className={INPUT} type="password" value={next} onChange={(e) => onNext(e.target.value)}
          autoComplete="new-password" autoFocus={autoFocus}
        />
      </Field>
      <Field label="Powtórz nowe hasło" hint={mismatch ? 'Hasła się różnią.' : undefined}>
        <input className={INPUT} type="password" value={repeat} onChange={(e) => onRepeat(e.target.value)} autoComplete="new-password" />
      </Field>
    </>
  )
}

/** Link do skopiowania (zaproszenie / reset) — pole tylko do odczytu + „Kopiuj link". */
export function CopyLink({ link }: { link: string }) {
  const [copied, setCopied] = useState<boolean | null>(null)
  const copy = () => {
    navigator.clipboard?.writeText(link).then(() => setCopied(true), () => setCopied(false))
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        readOnly value={link} aria-label="Link"
        onFocus={(e) => e.currentTarget.select()}
        className={`${INPUT} min-w-0 flex-1 font-mono text-xs`}
      />
      <button type="button" onClick={copy} className={BTN_SECONDARY}>
        {copied ? 'Skopiowany' : 'Kopiuj link'}
      </button>
      {copied === false && <span className="text-xs">Schowek niedostępny — zaznacz link i skopiuj ręcznie.</span>}
    </div>
  )
}

/** Role konta jako plakietki; `labels` daje nazwy i kolejność. Klucz spoza `labels`
 *  (rola usunięta albo nieznana) idzie na koniec, surowym kluczem. */
export function RoleChips({ roles, labels }: { roles: Role[]; labels: Record<Role, string> }) {
  if (roles.length === 0) return <span className="text-xs text-ink-400">bez ról</span>
  const ordered = [...Object.keys(labels).filter((k) => roles.includes(k)), ...roles.filter((r) => !(r in labels))]
  return (
    <span className="inline-flex flex-wrap gap-1">
      {ordered.map((r) => (
        <span
          key={r}
          className={`legia-chip px-2 py-0.5 text-[10px] ${
            r === ADMIN_ROLE ? 'bg-forest-800 text-white' : r in labels ? 'bg-forest-50 text-forest-800' : 'bg-ink-100 text-ink-500'
          }`}
          title={r in labels ? undefined : 'Rola, której nie ma już na liście ról'}
        >
          {labels[r] ?? r}
        </span>
      ))}
    </span>
  )
}

/** Role konta jako pola wyboru — kilka naraz, opcje i kolejność z API (/api/admin/roles). */
export function RolePicker({ value, onChange, options, disabled, lockAdmin }: {
  value: Role[]
  onChange: (roles: Role[]) => void
  options: RoleDef[] | null
  disabled?: boolean
  // Własne konto admina: rola admina zablokowana (backend i tak nie pozwoli jej zdjąć).
  lockAdmin?: boolean
}) {
  if (!options) return <span className="text-xs text-ink-400">Ładowanie ról…</span>
  // Wynik zawsze w kolejności listy i tylko z istniejących ról — klucz usuniętej roli odpada.
  const toggle = (r: Role) =>
    onChange(options.map((o) => o.key).filter((k) => (k === r ? !value.includes(r) : value.includes(k))))
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const on = value.includes(o.key)
        const locked = disabled || (lockAdmin && o.key === ADMIN_ROLE)
        return (
          <label
            key={o.key}
            title={lockAdmin && o.key === ADMIN_ROLE ? 'Nie możesz odebrać sobie roli admina' : o.description || undefined}
            className={[
              'inline-flex select-none items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
              on ? 'border-forest-700 bg-forest-50 text-forest-900' : 'border-ink-200 bg-white text-ink-500 hover:border-ink-300 hover:text-ink-800',
              locked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
            ].join(' ')}
          >
            <input
              type="checkbox"
              checked={on}
              disabled={locked}
              onChange={() => toggle(o.key)}
              className="h-3.5 w-3.5 accent-forest-800"
            />
            {o.label}
          </label>
        )
      })}
    </div>
  )
}

const LEVEL_TONE: Record<Level | 'none', string> = {
  none: 'bg-ink-100 text-ink-600',
  view: 'bg-forest-100 text-forest-900',
  edit: 'bg-forest-800 text-white',
}

/** Plakietka poziomu (Podgląd / Edycja) — np. na /konto przy sekcji. */
export function LevelChip({ level }: { level: Level }) {
  return <span className={`legia-chip px-2 py-0.5 text-[10px] ${LEVEL_TONE[level]}`}>{LEVEL_LABEL[level]}</span>
}

const LEVEL_OPTIONS: { value: Level | null; short: string; title: string }[] = [
  { value: null, short: '–', title: 'Brak dostępu' },
  { value: 'view', short: 'P', title: LEVEL_LABEL.view },
  { value: 'edit', short: 'E', title: LEVEL_LABEL.edit },
]

/** Komórka macierzy: trzy stany — brak / Podgląd / Edycja — jednym kliknięciem każdy. */
export function LevelSwitch({ value, onChange, disabled, label }: {
  value: Level | null
  onChange: (level: Level | null) => void
  disabled?: boolean
  // „Rola: Sekcja" — dla czytnika ekranu i podpowiedzi.
  label: string
}) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex overflow-hidden rounded-md border border-ink-200">
      {LEVEL_OPTIONS.map((o) => {
        const on = value === o.value
        return (
          <button
            key={o.short}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={disabled}
            title={`${label} — ${o.title}`}
            onClick={() => onChange(o.value)}
            className={[
              'w-6 py-0.5 text-[11px] font-semibold transition-colors disabled:cursor-not-allowed',
              on ? LEVEL_TONE[o.value ?? 'none'] : 'bg-white text-ink-300 hover:bg-ink-50 hover:text-ink-600',
            ].join(' ')}
          >
            {o.short}
          </button>
        )
      })}
    </div>
  )
}

/** Legenda skrótów w komórkach macierzy. */
export function LevelLegend() {
  return (
    <span className="inline-flex flex-wrap items-center gap-2 text-[11px] text-ink-500">
      {LEVEL_OPTIONS.map((o) => (
        <span key={o.short} className="inline-flex items-center gap-1">
          <span className={`inline-block w-5 rounded text-center font-semibold ${LEVEL_TONE[o.value ?? 'none']}`}>{o.short}</span>
          {o.title}
        </span>
      ))}
    </span>
  )
}
